import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldAlert, Clock3 } from "lucide-react";
import { api } from "@/lib/api";

const fmtFecha = (iso) => (iso || "").slice(0, 16).replace("T", " ");

const ESTADO = {
  ninguna: { tone: "border-emerald-500 bg-emerald-50", text: "text-emerald-800", icon: ShieldCheck, iconTone: "text-emerald-600" },
  media: { tone: "border-amber-500 bg-amber-50", text: "text-amber-800", icon: ShieldAlert, iconTone: "text-amber-600" },
  alta: { tone: "border-red-500 bg-red-50", text: "text-red-800", icon: ShieldAlert, iconTone: "text-red-600" },
};
const EBR_TONE = { Bajo: "bg-emerald-500", Medio: "bg-amber-500", Alto: "bg-red-500" };

// Pantalla de RESULTADO del cotejo en listas (Q-Detect + listas locales) tras el alta del
// prospecto. SOLO LECTURA: reutiliza el resultado ya persistido, sin reconsultar al proveedor
// ni consumir bolsa. Informativa: no frena el proceso (solo LPB confirmada por el OC bloquea).
export function ScreeningResultadoDialog({ prospectoId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !prospectoId) return;
    setData(null);
    setLoading(true);
    api.get(`/screening/prospecto/${prospectoId}/resultado`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, prospectoId]);

  const est = ESTADO[data?.criticidad] || ESTADO.ninguna;
  const Icon = est.icon;
  const grado = data?.ebr?.grado || "";
  // feb-2026 (P0-B) — Cuando el cotejo externo no completó y no hay coincidencias, la Pill
  // no puede ser verde: sería una mentira sobre datos no obtenidos.
  const pendienteReal = !!data?.screening_pendiente;
  const mostrarSinVerificar = pendienteReal && !data?.hay_coincidencias;
  const motivoPend = String(data?.screening_pendiente_motivo || "").split(":", 1)[0] || "sin_motivo";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="screening-resultado-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Resultado del cotejo en listas</DialogTitle>
        </DialogHeader>
        {loading && <p className="py-6 text-center text-sm text-slate-500">Consultando el resultado guardado…</p>}
        {!loading && !data && (
          <p className="py-6 text-center text-sm text-slate-500">No fue posible leer el resultado. El detalle está disponible en el botón “Riesgo PLD” del prospecto.</p>
        )}
        {!loading && data && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{data.nombre}</p>

            <div data-testid="scr-res-estado" className={`rounded-md border-l-4 px-4 py-3 ${mostrarSinVerificar ? "border-amber-500 bg-amber-50" : est.tone}`}>
              <div className="flex items-start gap-2.5">
                <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${mostrarSinVerificar ? "text-amber-600" : est.iconTone}`} />
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${mostrarSinVerificar ? "text-amber-800" : est.text}`}>
                    {data.hay_coincidencias
                      ? `Coincidencias detectadas (${data.coincidencias.length})`
                      : mostrarSinVerificar
                        ? `Sin verificar — cotejo externo pendiente (${motivoPend})`
                        : "Sin coincidencias en listas"}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-600">
                    Screening realizado el {fmtFecha(data.run?.fecha) || "—"}
                    {data.run?.folio ? <> · folio <span className="font-mono">{data.run.folio}</span></> : null}
                    {data.run?.listas_cruzadas ? ` · ${data.run.listas_cruzadas} lista(s) cruzada(s)` : null}
                  </p>
                </div>
              </div>
            </div>

            {data.screening_pendiente && (
              <div data-testid="scr-res-pendiente" className="flex items-start gap-2.5 rounded-md border-l-4 border-amber-500 bg-amber-50 px-4 py-3">
                <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-xs text-amber-800">
                  El cotejo contra el proveedor externo quedó pendiente y el sistema lo
                  reintentará automáticamente. El cotejo contra listas locales ya fue aplicado.
                  {data.screening_pendiente_motivo ? (
                    <> Motivo: <span className="font-mono">{motivoPend}</span>.</>
                  ) : null}
                </p>
              </div>
            )}

            {data.hay_coincidencias && (
              <div className="overflow-hidden rounded-md border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Lista</th>
                      <th className="px-3 py-2 font-medium">Categoría</th>
                      <th className="px-3 py-2 font-medium">PEP</th>
                      <th className="px-3 py-2 font-medium">Coincidencia</th>
                      <th className="px-3 py-2 font-medium">ID_PERSONA / Ref.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.coincidencias.map((m, i) => (
                      <tr key={i} data-testid="scr-res-coincidencia-row" className="border-t border-slate-100">
                        <td className="px-3 py-2 font-medium text-slate-700">{m.lista}<span className="ml-1.5 text-[10px] font-normal text-slate-400">{m.fuente}</span></td>
                        <td className="px-3 py-2 text-slate-600">{m.categoria_riesgo || "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{m.pep ? "Sí" : "No"}</td>
                        <td className="px-3 py-2 font-semibold text-slate-700">{m.score}%</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{m.id_persona || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div data-testid="scr-res-ebr" className="flex items-center gap-2.5 rounded-md border border-slate-200 px-4 py-3">
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${EBR_TONE[grado] || "bg-slate-300"}`} />
              <p className="text-sm text-slate-700">
                Evaluación Basada en Riesgos (EBR): <span className="font-semibold">{grado || "Sin calcular"}</span>
                {data.ebr?.puntaje != null && <span className="ml-1.5 text-xs text-slate-400">puntaje {data.ebr.puntaje}</span>}
              </p>
            </div>

            <p className="text-[11px] leading-relaxed text-slate-500">
              Resultado informativo: no detiene el proceso y la entidad conserva la decisión final. Las
              coincidencias quedan registradas para revisión del Oficial de Cumplimiento en el módulo de
              Cumplimiento PLD; únicamente una coincidencia confirmada en la Lista de Personas Bloqueadas
              (LPB) genera bloqueo. El detalle completo vive en el Perfil 360 y en Cumplimiento.
            </p>

            <div className="flex justify-end">
              <Button data-testid="scr-res-continuar" onClick={() => onOpenChange(false)} className="bg-primary hover:bg-sidebar-hover">
                Continuar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
